import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MdPublic, MdLocationCity, MdBarChart, MdSchool, MdSearch, MdLocationOn, MdEmojiEvents, MdStar, MdFilterList, MdClear, MdBusiness, MdCode, MdMenuBook, MdArticle, MdSportsEsports } from 'react-icons/md';
import { Link } from 'react-router-dom';
import CollegeSearchDropdown from './CollegeSearchDropdown';
import SearchableDropdown from './SearchableDropdown';
import ImageCarousel from './ImageCarousel';
import StarRating from './StarRating';
import PlacementPieChart from './PlacementPieChart';
import CollegeService from '../services/collegeService';
import { getShortCollegeName, resolveCollegeLogo } from '../utils/collegeLogo';

// Dynamic logo loading with Cloudinary fallback
const CollegeLogo = React.memo(({ collegeName, collegeData, style, alt }) => {
  const [logoSrc, setLogoSrc] = useState(null);
  const [showShortName, setShowShortName] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!collegeName) return;

    let cancelled = false;

    const loadLogo = async () => {
      setLoading(true);

      const resolution = await resolveCollegeLogo(collegeName, collegeData?.logo_url);
      if (cancelled) {
        return;
      }

      if (resolution.mode === 'image') {
        setLogoSrc(resolution.src);
        setShowShortName(false);
      } else {
        setLogoSrc(null);
        setShowShortName(true);
      }

      setLoading(false);
    };

    loadLogo();

    return () => {
      cancelled = true;
    };
  }, [collegeName, collegeData?.logo_url]);
  
  if (loading) {
    return (
      <div 
        style={{
          ...style,
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div className="w-4 h-4 border-2 border-gray-300 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (showShortName) {
    const shortName = getShortCollegeName(collegeName, collegeData?.short_name);
    
    return (
      <div 
        style={{
          ...style,
          backgroundColor: '#6366f1',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: style?.width ? `${parseInt(style.width) * 0.2}px` : '14px',
          fontWeight: 'bold',
          borderRadius: '8px'
        }}
      >
        {shortName}
      </div>
    );
  }
  
  if (!logoSrc) {
    return <div style={{...style, backgroundColor: '#f0f0f0'}} />;
  }
  
  return (
    <img 
      src={logoSrc}
      style={style} 
      alt={alt}
      onError={() => {
        setShowShortName(true);
        setLogoSrc(null);
      }}
    />
  );
});

function CollegeProfile() {
  const [allColleges] = useState([]);
  const [selected, setSelected] = useState('');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(0);

  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultCount, setSearchResultCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const [states, setStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const searchRef = useRef(null);
  const headerRef = useRef(null);
  const profileRef = useRef(null); // New ref for profile section
  const [isHeaderCompact, setIsHeaderCompact] = useState(false);

  const navigate = useNavigate();
  const { slug } = useParams(); // Get slug from URL
  const [searchParams] = useSearchParams();
  const collegeIdFromQuery = searchParams.get('collegeId');

  // Helper functions
  const generateSlug = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Reusable college field getters
  const getCollegeField = (college, field, defaultValue = '') => {
    return college?.[field] || defaultValue;
  };

  // Load filter metadata
  const searchColleges = useCallback(async (query, limit = 10) => {
    return CollegeService.searchColleges({
      query,
      state: stateFilter,
      city: cityFilter,
      limit
    });
  }, [cityFilter, stateFilter]);

  const scrollToProfile = useCallback(() => {
    if (profileRef.current) {
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        setTimeout(() => {
          profileRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoading(true);
        const filterData = await CollegeService.getCollegeFilters();
        setStates(filterData.states || []);
        setAvailableCities(filterData.cities || []);
      } catch (err) {
        console.error('Error loading colleges:', err);
        // Use sample data as fallback
        const sampleData = [
          { college_name: "Indian Institute of Technology Delhi", state: "Delhi", district: "New Delhi", type: "Autonomous", year_established: 1961 },
          { college_name: "Indian Institute of Technology Bombay", state: "Maharashtra", district: "Mumbai", type: "Autonomous", year_established: 1958 },
          { college_name: "Indian Institute of Science Bangalore", state: "Karnataka", district: "Bangalore", type: "Deemed University", year_established: 1909 }
        ];
        setAllColleges(sampleData);
        setDisplayedColleges(sampleData);
      } finally {
        setLoading(false);
      }
    };
    
    loadFilters();
  }, []);

  // Load college from URL slug (NEW FUNCTIONALITY)
  useEffect(() => {
    const loadCollegeFromSlug = async () => {
      if (collegeIdFromQuery) {
        return;
      }

      if (slug) {
        try {
          const college = await CollegeService.getCollegeBySlug(slug);
          if (college) {
            setSelected(college.college_name);
            setProfile(college);
            setSearchQuery(college.college_name);
            scrollToTop();
          }
        } catch (error) {
          console.error('Failed to load college from slug:', error);
        }
        return;
      }
    };
    
    loadCollegeFromSlug();
  }, [slug, collegeIdFromQuery, scrollToTop]);


  // Handle search results for live dropdown
  useEffect(() => {
    const loadSearchResults = async () => {
      const trimmedQuery = searchQuery.trim();
      if (!trimmedQuery) {
        setSearchResults([]);
        setSearchResultCount(0);
        setShowSearchResults(false);
        setIsSearching(false);
        return;
      }

      try {
        setIsSearching(true);
        const response = await searchColleges(trimmedQuery, 10);
        setSearchResults(response.data || []);
        setSearchResultCount(response.total || 0);
        setShowSearchResults((response.data || []).length > 0);
      } catch (error) {
        console.error('Error searching colleges:', error);
        setSearchResults([]);
        setSearchResultCount(0);
        setShowSearchResults(false);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = window.setTimeout(loadSearchResults, 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchColleges, searchQuery]);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const filterData = await CollegeService.getCollegeFilters({ state: stateFilter });
        setAvailableCities(filterData.cities || []);
        if (cityFilter && !(filterData.cities || []).includes(cityFilter)) {
          setCityFilter('');
        }
      } catch (error) {
        console.error('Error loading cities:', error);
        setAvailableCities([]);
      }
    };

    loadCities();
  }, [cityFilter, stateFilter]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle header scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const scrolled = window.scrollY > 100;
        setIsHeaderCompact(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const clearFilters = () => {
    setStateFilter('');
    setCityFilter('');
    setSearchQuery('');
    setSelected('');
    setProfile(null);
    setSearchResults([]);
    setSearchResultCount(0);
    setShowSearchResults(false);
  };

  useEffect(() => {
    const loadCollegeFromId = async () => {
      if (!collegeIdFromQuery) {
        return;
      }

      try {
        const college = await CollegeService.getCollegeById(collegeIdFromQuery);

        if (!college) {
          return;
        }

        setSelected(college.college_name);
        setProfile(college);
        setSearchQuery(college.college_name);
        scrollToTop();
      } catch (error) {
        console.error('Failed to load college from query id:', error);
      }
    };

    loadCollegeFromId();
  }, [collegeIdFromQuery, scrollToTop]);

  const handleCollegeSelect = async (selectedCollege) => {
    const collegeName = selectedCollege?.college_name || selectedCollege?.name || '';
    setSelected(collegeName);

    if (selectedCollege?._id) {
      try {
        setLoading(true);
        const college = await CollegeService.getCollegeById(selectedCollege._id);
        setProfile(college || null);
        setSearchQuery(collegeName);
        setShowSearchResults(false);
        setUserRating(0);
        
        if (college) {
          const collegeSlug = college.slug || generateSlug(college.college_name);
          navigate(`/college-profile/${collegeSlug}?collegeId=${encodeURIComponent(college._id)}`, { replace: true });
        }
        
        // Auto scroll to top when viewing full profile
        scrollToTop();
      } catch (error) {
        console.error('Failed to load selected college:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setProfile(null);
      setUserRating(0);
      navigate('/college-profile', { replace: true });
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleCollegeSelect(searchResults[0]);
      // Auto-scroll will be triggered by handleCollegeSelect
    }
    if (e.key === 'Escape') {
      setShowSearchResults(false);
    }
  };

  // Handle user rating change
  const handleRatingChange = (newRating) => {
    setUserRating(newRating);
    // Here you can add API call to submit the rating

  };

  const getStoredRatingSummary = useCallback(() => {
    const rawRatingText =
      profile?.student_reviews_ratings ||
      profile?.reviews_ratings ||
      profile?.rating ||
      '';

    if (!rawRatingText) {
      return { numericRating: null, reviewText: '' };
    }

    const ratingMatch = String(rawRatingText).match(/(\d+(?:\.\d+)?)\s*\/\s*5|(\d+(?:\.\d+)?)/);
    const numericRating = ratingMatch
      ? parseFloat(ratingMatch[1] || ratingMatch[2])
      : null;

    return {
      numericRating: Number.isNaN(numericRating) ? null : numericRating,
      reviewText: String(rawRatingText).trim()
    };
  }, [profile]);

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) => 
      regex.test(part) ? 
        <span key={index} className="bg-yellow-200 font-semibold">{part}</span> : 
        part
    );
  };

  // Simplified field accessors using the generic helper
  const getCollegeState = (college) => getCollegeField(college, 'state');
  const getCollegeCity = (college) => getCollegeField(college, 'district');
  const getCollegeName = (college) => getCollegeField(college, 'college_name', 'Unknown College');

  // Get real images from MongoDB with optimized quality
  const getCollegeImages = (collegeData, category, count = 6) => {
    const categoryMap = {
      'Location & Campus': 'campus',
      'Infrastructure & Facilities': 'infrastructure', 
      'Academics': 'faculty'
    };
    
    const dbCategory = categoryMap[category];
    const realImages = collegeData?.images?.[dbCategory] || [];
    
    // Convert MongoDB images to required format with quality optimization
    const formattedImages = realImages.slice(0, count).map((img, index) => {
      let optimizedUrl = img?.secure_url || '';
      
      // Add Cloudinary quality optimization if it's a Cloudinary URL
      if (optimizedUrl.includes('cloudinary.com')) {
        optimizedUrl = optimizedUrl.replace('/upload/', '/upload/q_auto:best,f_auto/');
      }
      
      return {
        url: optimizedUrl,
        alt: category === 'Academics' ? `Faculty ${index + 1}` : `${category} ${index + 1}`,
        caption: category === 'Academics' ? `Faculty ${index + 1}` : `${category} View ${index + 1}`,
        loading: 'lazy'
      };
    }).filter(img => img.url); // Remove empty URLs
    
    // If real images found, return only real images (don't mix with defaults)
    if (formattedImages.length > 0) {
      return formattedImages;
    }
    
    // Only add default images if NO real images found
    return getDefaultImages(category, 3); // Show only 3 default images
  };

  // Generate default placeholder images from local assets
  const getDefaultImages = (category, count) => {
    const defaultImageSets = {
      'Location & Campus': [
        '/default-images/campus/default_campus.png'
      ],
      'Infrastructure & Facilities': [
        '/default-images/infrastructure/default_infra1.png',
        '/default-images/infrastructure/default_infra2.png'
      ],
      'Academics': [
        '/default-images/faculty/Default_faculty1.png',
        '/default-images/faculty/Defaulty_faculty2.png'
      ]
    };

    const categoryImages = defaultImageSets[category] || defaultImageSets['Location & Campus'];
    
    return categoryImages.slice(0, count).map((imageUrl, index) => ({
      url: imageUrl,
      alt: category === 'Academics' ? `Faculty ${index + 1}` : `${category} ${index + 1}`,
      caption: category === 'Academics' ? `Faculty ${index + 1}` : `${category} View ${index + 1}`,
      loading: 'lazy',
      isDefault: true
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/favicon.png" 
                alt="Logo" 
                className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain"
              />
              <div>
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent leading-normal pb-1">
                  College Profile Viewer
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">
                  {loading ? 'Loading colleges...' : 'Discover colleges across India'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <Link
                to="/tutorials"
                className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
                style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
              >
                <MdMenuBook className="w-3 h-3 sm:w-4 sm:h-4" />
                Tutorials
              </Link>
            <Link
              to="/blogs"
              className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
              style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
            >
                <MdArticle className="w-3 h-3 sm:w-4 sm:h-4" />
                Blogs
              </Link>
            <Link
              to="/practice-arena"
              className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
              style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
            >
                <MdSportsEsports className="w-3 h-3 sm:w-4 sm:h-4" />
                Practice Arena
              </Link>
            <button
              onClick={() => navigate('/compiler', { state: { from: `${window.location.pathname}${window.location.search}` } })}
              className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
              style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
            >
                <MdCode className="w-3 h-3 sm:w-4 sm:h-4" />
                Compiler
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
                style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Search and Filters - Left Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 sticky top-4 max-h-[calc(100vh-2rem)] overflow-visible">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                    <MdFilterList className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-800">Search & Filter</h2>
                </div>
                <button
                  onClick={clearFilters}
                  className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <MdClear className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              </div>
              
              <div className="overflow-y-auto max-h-[calc(100vh-8rem)]">
                {/* Search */}
                <div className="mb-4 sm:mb-6 px-1" ref={searchRef}>
                <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2 mb-2 sm:mb-3">
                  <MdSearch className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                  Search Colleges
                </label>
                <div className="relative">
                  <MdSearch className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => (searchResults.length > 0 || isSearching) && setShowSearchResults(true)}
                    placeholder="Type college name, state, or city..."
                    className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-0 focus:border-purple-500 transition-all shadow-sm text-sm sm:text-base"
                  />
                  
                  {/* Live Search Results Dropdown */}
                  {showSearchResults && (searchResults.length > 0 || isSearching) && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg sm:rounded-xl shadow-lg z-50 max-h-60 sm:max-h-80 overflow-y-auto">
                      {isSearching ? (
                        <div className="p-3 sm:p-4 text-sm text-gray-500">Searching colleges...</div>
                      ) : searchResults.map((college, index) => (
                        <div
                          key={college.college_name}
                          onClick={() => handleCollegeSelect(college)}
                          className={`p-3 sm:p-4 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                            index === 0 ? 'bg-purple-25' : ''
                          }`}
                        >
                          <div className="font-medium text-gray-900 mb-1 text-sm sm:text-base">
                            {highlightMatch(college.college_name, searchQuery)}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                            <MdLocationOn className="w-3 h-3 sm:w-4 sm:h-4" />
                            {college.district && college.state ? 
                              `${college.district}, ${college.state}` : 
                              college.state || college.district || 'Location not specified'
                            }
                          </div>
                        </div>
                      ))}
                      {searchResults.length === 10 && (
                        <div className="p-2 sm:p-3 text-center text-xs sm:text-sm text-gray-500 bg-gray-50">
                          Showing top 10 results. Type more to refine search.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {searchQuery && !showSearchResults && (
                  <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-purple-700 font-medium">
                      {searchResults.length === 0 ? 'No colleges found' : 
                        `${searchResultCount} college${searchResultCount !== 1 ? 's' : ''} found`
                      }
                    </p>
                  </div>
                )}
              </div>

                {/* Filter Section */}
                <div className="border-t border-gray-100 pt-4 sm:pt-6">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-3 sm:mb-4 uppercase tracking-wide">Filter Options</h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {/* State Filter */}
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <MdPublic className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                        State ({states.length})
                      </label>
                      <SearchableDropdown
                        options={['', ...states]}
                        value={stateFilter}
                        onChange={setStateFilter}
                        placeholder="All States"
                        disabled={loading}
                      />
                    </div>

                    {/* District Filter */}
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <MdLocationCity className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                        District ({availableCities.length})
                      </label>
                      <SearchableDropdown
                        options={['', ...availableCities]}
                        value={cityFilter}
                        onChange={setCityFilter}
                        placeholder="All Districts"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                {/* Manual College Selection */}
                <div className="mt-4 sm:mt-6">
                  <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <MdSchool className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                    Select College
                  </label>
                  <CollegeSearchDropdown
                    onSearch={(query) => searchColleges(query, 10).then((response) => response.data || [])}
                    value={profile}
                    onChange={handleCollegeSelect}
                    placeholder="Search and choose a college..."
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Profile Display - Right Side */}
          <div className="lg:col-span-3" ref={profileRef}>
            {loading ? (
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 lg:p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 border-b-2 border-purple-600 mx-auto mb-3 sm:mb-4"></div>
                <p className="text-gray-600 text-sm sm:text-base">Loading colleges...</p>
              </div>
            ) : profile ? (
              <div className="space-y-0">
                {/* Sticky Profile Header */}
                <div 
                  ref={headerRef}
                  className={`sticky top-0 z-10 transition-all duration-300 ${
                    isHeaderCompact ? 'shadow-lg' : 'shadow-sm'
                  }`}
                >
                  <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white overflow-hidden">
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                    <div className={`relative transition-all duration-300 ${
                      isHeaderCompact 
                        ? 'px-3 py-2 sm:px-6 sm:py-3' 
                        : 'px-3 py-3 sm:px-6 sm:py-4'
                    }`}>
                      <div className="max-w-4xl mx-auto">
                        {/* Badge - Hidden on mobile, visible on tablet+ */}
                        <div className={`hidden sm:inline-flex items-center px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-medium transition-all duration-300 ${
                          isHeaderCompact ? 'mb-2' : 'mb-3'
                        }`}>
                          <MdSchool className="w-3 h-3 mr-1.5" />
                          College Profile
                        </div>
                        
                        {/* Header Layout - Mobile: Simple, Tablet+: Full */}
                        <div className={`flex items-start transition-all duration-300 ${
                          isHeaderCompact ? 'gap-2 sm:gap-4 mb-2 sm:mb-3' : 'gap-3 sm:gap-4 mb-3 sm:mb-4'
                        }`}>
                          <CollegeLogo
                            collegeName={profile.college_name}
                            collegeData={profile}
                            alt={`${getCollegeName(profile)} logo`}
                            style={{
                              width: isHeaderCompact ? '40px' : '48px',
                              height: isHeaderCompact ? '40px' : '48px',
                              objectFit: 'contain',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              padding: '6px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              flexShrink: 0
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            {/* College Name - Always visible */}
                            <h2 className={`font-bold leading-tight transition-all duration-300 text-white ${
                              isHeaderCompact 
                                ? 'text-sm sm:text-lg' 
                                : 'text-base sm:text-xl lg:text-2xl'
                            }`}>
                              {getCollegeName(profile)}
                            </h2>
                            {/* Short Name - Show on mobile, visible on all screens */}
                            {profile.short_name && (
                              <p className={`text-white opacity-90 font-medium transition-all duration-300 ${
                                isHeaderCompact ? 'text-xs sm:text-sm' : 'text-xs sm:text-base'
                              }`}>
                                ({profile.short_name})
                              </p>
                            )}
                            {/* Location - Show on mobile, hidden on tablet+ (since it's in stats grid) */}
                            {getCollegeCity(profile) && getCollegeState(profile) && (
                              <p className={`sm:hidden text-white opacity-80 font-normal transition-all duration-300 flex items-center gap-1 ${
                                isHeaderCompact ? 'text-xs' : 'text-xs'
                              }`}>
                                <MdLocationOn className="w-3 h-3" />
                                {getCollegeCity(profile)}, {getCollegeState(profile)}
                              </p>
                            )}
                            {/* University Name - Hidden on mobile, visible on tablet+ */}
                            {profile.universityName && (
                              <p className={`hidden sm:block text-white opacity-80 font-normal transition-all duration-300 ${
                                isHeaderCompact ? 'text-xs sm:text-sm' : 'text-xs sm:text-base'
                              }`}>
                                Affiliated to: {profile.universityName}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Stats Grid - Hidden on mobile, visible on tablet+ */}
                        <div className={`hidden sm:grid text-white transition-all duration-300 ${
                          isHeaderCompact 
                            ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 sm:gap-2' 
                            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3'
                        }`}>
                          {getCollegeCity(profile) && getCollegeState(profile) && (
                            <div className={`flex items-center bg-white bg-opacity-15 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                              isHeaderCompact ? 'gap-1 p-1.5 sm:gap-2 sm:p-2' : 'gap-2 p-2 sm:p-3'
                            }`}>
                              <div className={`bg-white bg-opacity-25 rounded-md flex-shrink-0 ${
                                isHeaderCompact ? 'p-1 sm:p-1.5' : 'p-1.5'
                              }`}>
                                <MdLocationOn className={`${
                                  isHeaderCompact ? 'w-2.5 h-2.5 sm:w-3 sm:h-3' : 'w-3 h-3'
                                }`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`font-medium opacity-90 mb-0.5 ${
                                  isHeaderCompact ? 'text-xs' : 'text-xs'
                                }`}>Location</p>
                                <p className={`font-semibold leading-tight ${
                                  isHeaderCompact ? 'text-xs sm:text-sm' : 'text-xs sm:text-sm'
                                }`}>{getCollegeCity(profile)}</p>
                                <p className={`font-medium opacity-80 ${
                                  isHeaderCompact ? 'text-xs' : 'text-xs'
                                }`}>{getCollegeState(profile)}</p>
                              </div>
                            </div>
                          )}
                          {profile.year_established && (
                            <div className={`flex items-center bg-white bg-opacity-15 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                              isHeaderCompact ? 'gap-1 p-1.5 sm:gap-2 sm:p-2' : 'gap-2 p-2 sm:p-3'
                            }`}>
                              <div className={`bg-white bg-opacity-25 rounded-md flex-shrink-0 ${
                                isHeaderCompact ? 'p-1 sm:p-1.5' : 'p-1.5'
                              }`}>
                                <MdEmojiEvents className={`${
                                  isHeaderCompact ? 'w-2.5 h-2.5 sm:w-3 sm:h-3' : 'w-3 h-3'
                                }`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`font-medium opacity-90 mb-0.5 ${
                                  isHeaderCompact ? 'text-xs' : 'text-xs'
                                }`}>Established</p>
                                <p className={`font-semibold ${
                                  isHeaderCompact ? 'text-xs sm:text-sm' : 'text-xs sm:text-sm'
                                }`}>{profile.year_established}</p>
                              </div>
                            </div>
                          )}
                          {(profile.naac_grade || profile.overall_rating) && (
                            <div className={`flex items-center bg-white bg-opacity-15 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                              isHeaderCompact ? 'gap-1 p-1.5 sm:gap-2 sm:p-2' : 'gap-2 p-2 sm:p-3'
                            }`}>
                              <div className={`bg-white bg-opacity-25 rounded-md flex-shrink-0 ${
                                isHeaderCompact ? 'p-1 sm:p-1.5' : 'p-1.5'
                              }`}>
                                <MdStar className={`${
                                  isHeaderCompact ? 'w-2.5 h-2.5 sm:w-3 sm:h-3' : 'w-3 h-3'
                                }`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`font-medium opacity-90 mb-0.5 ${
                                  isHeaderCompact ? 'text-xs' : 'text-xs'
                                }`}>{profile.naac_grade ? 'NAAC Grade' : 'Rating'}</p>
                                <p className={`font-semibold ${
                                  isHeaderCompact ? 'text-xs sm:text-sm' : 'text-xs sm:text-sm'
                                }`}>{profile.naac_grade || profile.overall_rating}</p>
                              </div>
                            </div>
                          )}
                          {profile.average_salary && (
                            <div className={`flex items-center bg-white bg-opacity-15 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                              isHeaderCompact ? 'gap-1 p-1.5 sm:gap-2 sm:p-2' : 'gap-2 p-2 sm:p-3'
                            }`}>
                              <div className={`bg-white bg-opacity-25 rounded-md flex-shrink-0 ${
                                isHeaderCompact ? 'p-1 sm:p-1.5' : 'p-1.5'
                              }`}>
                                <MdBarChart className={`${
                                  isHeaderCompact ? 'w-2.5 h-2.5 sm:w-3 sm:h-3' : 'w-3 h-3'
                                }`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`font-medium opacity-90 mb-0.5 ${
                                  isHeaderCompact ? 'text-xs' : 'text-xs'
                                }`}>Avg Salary</p>
                                <p className={`font-semibold leading-tight ${
                                  isHeaderCompact ? 'text-xs sm:text-sm' : 'text-xs sm:text-sm'
                                }`}>
                                  {(() => {
                                    const salaryValue = profile.average_salary;
                                    let salaryText = String(salaryValue);
                                    if (!salaryText.toLowerCase().includes('lpa') && !salaryText.toLowerCase().includes('₹')) {
                                      salaryText = `${salaryValue} LPA`;
                                    }
                                    return salaryText;
                                  })()}
                                </p>
                              </div>
                            </div>
                          )}
                          {profile.type && (
                            <div className={`flex items-center bg-white bg-opacity-15 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                              isHeaderCompact ? 'gap-1 p-1.5 sm:gap-2 sm:p-2' : 'gap-2 p-2 sm:p-3'
                            }`}>
                              <div className={`bg-white bg-opacity-25 rounded-md flex-shrink-0 ${
                                isHeaderCompact ? 'p-1 sm:p-1.5' : 'p-1.5'
                              }`}>
                                <MdSchool className={`${
                                  isHeaderCompact ? 'w-2.5 h-2.5 sm:w-3 sm:h-3' : 'w-3 h-3'
                                }`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`font-medium opacity-90 mb-0.5 ${
                                  isHeaderCompact ? 'text-xs' : 'text-xs'
                                }`}>Type</p>
                                <p className={`font-semibold leading-tight ${
                                  isHeaderCompact ? 'text-xs sm:text-sm' : 'text-xs sm:text-sm'
                                }`}>{profile.type}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profile Content - Categorized Sections */}
                <div className="bg-white rounded-b-xl sm:rounded-b-2xl shadow-lg border border-gray-100 border-t-0">
                  <div className="p-4 sm:p-6 lg:p-8">
                    {(() => {
                      // Categorize profile data with improved structure
                      const categoryConfig = {
                        'Basic Information': {
                          fields: ['type', 'ownership', 'university_type', 'affiliation', 'universityName', 'short_name', 'year_established'],
                          icon: MdSchool,
                          keywords: ['type', 'category', 'status', 'university', 'affiliation']
                        },
                        'Contact Information': {
                          fields: ['contact_number', 'contact_email', 'phone', 'website'],
                          icon: MdBusiness,
                          keywords: ['contact', 'email', 'phone', 'website', 'communication']
                        },
                        'Location & Campus': {
                          fields: ['state', 'district', 'address', 'campus_size', 'location', 'city', 'pincode'],
                          icon: MdLocationOn,
                          keywords: ['location', 'address', 'campus', 'city', 'area']
                        },
                        'Academics': {
                          fields: ['departments', 'courses', 'programs', 'specializations', 'faculty', 'total_faculty', 'student_faculty_ratio', 'course', 'program', 'degree', 'degrees', 'streams', 'branches', 'subjects'],
                          icon: MdSchool,
                          keywords: ['course', 'program', 'department', 'faculty', 'academic', 'degree', 'stream', 'branch', 'subject', 'specialization']
                        },
                        'Fees & Admission': {
                          fields: ['fees', 'admission', 'eligibility', 'entrance_exam', 'application', 'admission_process', 'cutoff', 'seats'],
                          icon: MdBarChart,
                          keywords: ['fee', 'admission', 'eligibility', 'entrance', 'cutoff', 'seats']
                        },
                        'Placements & Salary': {
                          fields: ['placement', 'recruiters', 'average_package', 'highest_package', 'placement_rate', 'average_salary', 'highest_salary', 'median_salary', 'placement_percentage', 'top_recruiters', 'avg_salary', 'max_salary', 'min_salary', 'avg_package', 'max_package', 'placement_salary', 'salary_avg', 'salary_max', 'package_avg', 'package_max', 'ctc_avg', 'ctc_max'],
                          icon: MdBarChart,
                          keywords: ['placement', 'package', 'recruit', 'job', 'salary', 'ctc', 'stipend', 'lpa']
                        },
                        'Infrastructure & Facilities': {
                          fields: ['facilities', 'hostel', 'library', 'labs', 'sports', 'infrastructure', 'hostel_facilities', 'transport', 'cafeteria', 'medical_facilities'],
                          icon: MdPublic,
                          keywords: ['facility', 'hostel', 'library', 'lab', 'infrastructure', 'transport']
                        },
                        'Financial Aid & Scholarships': {
                          fields: ['scholarship', 'scholarships', 'financial_aid', 'fee_waiver', 'grants', 'loan_facilities'],
                          icon: MdBarChart,
                          keywords: ['scholarship', 'financial', 'aid', 'grant', 'loan', 'waiver']
                        },
                        'Rankings & Accreditation': {
                          fields: ['ranking', 'naac_grade', 'accreditation', 'awards', 'recognition', 'nirf_ranking'],
                          icon: MdEmojiEvents,
                          keywords: ['rank', 'grade', 'accredit', 'award', 'recognition']
                        },
                        'Reviews & Ratings': {
                          fields: ['review', 'reviews', 'rating', 'ratings', 'student_reviews', 'overall_rating', 'academic_rating', 'placement_rating', 'infrastructure_rating'],
                          icon: MdSchool,
                          keywords: ['review', 'rating', 'feedback', 'student_review']
                        },
                        'Notable Alumni': {
                          fields: ['notable_alumni', 'notable_alumini', 'famous_alumni', 'alumni', 'distinguished_alumni'],
                          icon: MdEmojiEvents,
                          keywords: ['alumni', 'notable', 'famous', 'distinguished']
                        }
                      };

                      const getIcon = (category) => categoryConfig[category]?.icon || MdSchool;

                      // Improved category data extraction
                      const getCategoryData = (categoryName, config) => {
                        const categoryData = config.fields.reduce((acc, field) => {
                          if (profile[field] && profile[field] !== null) {
                            acc[field] = profile[field];
                          }
                          return acc;
                        }, {});

                        // Handle nested contact object for Contact Information
                        if (categoryName === 'Contact Information' && profile.contact) {
                          if (profile.contact.phones && Array.isArray(profile.contact.phones)) {
                            categoryData['phones'] = profile.contact.phones;
                          }
                          if (profile.contact.email) {
                            categoryData['email'] = profile.contact.email;
                          }
                        }

                        // Add uncategorized fields based on keywords
                        Object.entries(profile).forEach(([key, value]) => {
                          if (!value || key === 'college_name' || key === 'name' || key === 'year_established' || key === 'contact') return;
                          
                          const keyLower = key.toLowerCase();
                          const isAlreadyCategorized = Object.values(categoryConfig)
                            .some(cat => cat.fields.includes(key));
                          
                          if (!isAlreadyCategorized && config.keywords.some(keyword => keyLower.includes(keyword))) {
                            categoryData[key] = value;
                          }
                        });

                        return categoryData;
                      };

                      const formatValue = (val, fieldKey) => {
                        if (!val) return null;
                        
                        // Clean string values that might contain stray braces
                        if (typeof val === 'string') {
                          const cleanedVal = val.replace(/^\s*}\s*/, '').replace(/\s*}\s*$/, '').trim();
                          if (cleanedVal !== val) {
                            val = cleanedVal;
                          }
                        }
                        
                        if (typeof val === 'object') {
                          if (Array.isArray(val)) {
                            // Clean array items
                            const cleanedArray = val.map(item => {
                              if (typeof item === 'string') {
                                return item.replace(/^\s*}\s*/, '').replace(/\s*}\s*$/, '').trim();
                              }
                              return item;
                            }).filter(item => item && item !== '}');
                            
                            // Special handling for alumni, reviews, etc.
                            if (fieldKey?.toLowerCase().includes('alumni') || fieldKey?.toLowerCase().includes('review')) {
                              return (
                                <div className="space-y-2">
                                  {cleanedArray.map((item, idx) => (
                                    <div key={idx} className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                                      <span className="text-purple-700 font-medium">{String(item)}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            
                            // Special handling for contact fields (phones, emails)
                            if (fieldKey?.toLowerCase().includes('phone') || fieldKey?.toLowerCase().includes('contact') || fieldKey?.toLowerCase().includes('email')) {
                              return (
                                <div className="space-y-2">
                                  {cleanedArray.map((item, idx) => {
                                    if (fieldKey?.toLowerCase().includes('email')) {
                                      return (
                                        <div key={idx} className="w-full">
                                          <a 
                                            href={`mailto:${item}`} 
                                            className="text-blue-600 hover:text-blue-800 underline break-all text-sm leading-relaxed block"
                                            title={String(item)}
                                          >
                                            {String(item)}
                                          </a>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div key={idx} className="w-full">
                                          <a 
                                            href={`tel:${item}`} 
                                            className="text-blue-600 hover:text-blue-800 underline text-sm font-medium block"
                                          >
                                            {String(item)}
                                          </a>
                                        </div>
                                      );
                                    }
                                  })}
                                </div>
                              );
                            }
                            
                            return (
                              <div className="flex flex-wrap gap-2">
                                {cleanedArray.map((item, idx) => (
                                  <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                    {String(item)}
                                  </span>
                                ))}
                              </div>
                            );
                          }
                          
                          // Handle object type - convert to string representation
                          return (
                            <div className="w-full">
                              <p className="text-gray-700 leading-relaxed text-sm">{JSON.stringify(val, null, 2)}</p>
                            </div>
                          );
                        }
                        
                        // Special formatting for specific field types
                        if (fieldKey?.toLowerCase().includes('salary') || fieldKey?.toLowerCase().includes('package')) {
                          // Handle both numeric and string salary values, add LPA if not present
                          let salaryText = String(val);
                          if (!salaryText.toLowerCase().includes('lpa') && !salaryText.toLowerCase().includes('₹')) {
                            salaryText = `${val} LPA`;
                          }
                          return (
                            <div className="w-full">
                              <p className="text-purple-600 leading-relaxed font-semibold text-sm">{salaryText}</p>
                            </div>
                          );
                        }
                        
                        if (fieldKey?.toLowerCase().includes('rating') || fieldKey?.toLowerCase().includes('grade')) {
                          // Handle rating fields with star display
                          const numericRating = parseFloat(val);
                          if (!isNaN(numericRating) && numericRating >= 0 && numericRating <= 5) {
                            return (
                              <div className="w-full">
                                <StarRating 
                                  rating={numericRating} 
                                  size="small" 
                                  interactive={false}
                                  showNumeric={true}
                                  className="mb-2"
                                />
                                <span className="text-gray-700 leading-relaxed font-semibold text-sm block">{val}</span>
                              </div>
                            );
                          }
                          return (
                            <div className="w-full">
                              <span className="text-gray-700 leading-relaxed font-semibold text-sm">{val}</span>
                            </div>
                          );
                        }
                        
                        if (fieldKey?.toLowerCase().includes('email')) {
                          return (
                            <div className="w-full">
                              <a 
                                href={`mailto:${val}`} 
                                className="text-blue-600 hover:text-blue-800 underline break-all text-sm leading-relaxed block py-1"
                                title={val}
                              >
                                {val}
                              </a>
                            </div>
                          );
                        }
                        
                        if (fieldKey?.toLowerCase().includes('phone') || fieldKey?.toLowerCase().includes('contact')) {
                          return (
                            <div className="w-full">
                              <a 
                                href={`tel:${val}`} 
                                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium block py-1"
                              >
                                {val}
                              </a>
                            </div>
                          );
                        }
                        
                        if (fieldKey?.toLowerCase().includes('website')) {
                          // Ensure proper URL format to prevent internal redirects
                          let websiteUrl = val;
                          if (websiteUrl && !websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
                            websiteUrl = `https://${websiteUrl}`;
                          }
                          return (
                            <div className="w-full">
                              <a 
                                href={websiteUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-600 hover:text-blue-800 underline break-all text-sm leading-relaxed inline-block w-full"
                                title={val}
                              >
                                {val}
                              </a>
                            </div>
                          );
                        }
                        
                        if (fieldKey?.toLowerCase().includes('address')) {
                          return (
                            <div className="w-full">
                              <p className="text-gray-700 leading-relaxed text-sm">{val}</p>
                            </div>
                          );
                        }
                        
                        // Clean string values before final rendering
                        const cleanedStringVal = String(val).replace(/^\s*}\s*/, '').replace(/\s*}\s*$/, '').trim();
                        
                        return (
                          <div className="w-full">
                            <p className="text-gray-700 leading-relaxed text-sm">{cleanedStringVal}</p>
                          </div>
                        );
                      };

                      return (
                        <div className="space-y-8">
                          {Object.entries(categoryConfig).map(([categoryName, config]) => {
                            const categoryData = getCategoryData(categoryName, config);
                            const visibleCategoryEntries = Object.entries(categoryData).filter(([key]) => {
                              if (key === 'courses' && categoryData['programs'] && Array.isArray(categoryData['programs']) && categoryData['programs'].length > 0) {
                                return false;
                              }

                              if (categoryName === 'Reviews & Ratings' && key === 'student_reviews_ratings') {
                                return false;
                              }

                              return true;
                            });

                            const showDetailsGrid = visibleCategoryEntries.length > 0;
                            const showRatingCard = categoryName === 'Reviews & Ratings';

                            if (!showRatingCard && !showDetailsGrid) return null;

                            return (
                              <div key={categoryName} className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 pb-2 sm:pb-3 border-b border-gray-200">
                                  <div className="p-2 sm:p-3 bg-purple-100 rounded-lg sm:rounded-xl">
                                    {React.createElement(getIcon(categoryName), { className: "w-4 h-4 sm:w-6 sm:h-6 text-purple-600" })}
                                  </div>
                                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{categoryName}</h3>
                                </div>
                                
                                {/* Interactive Rating Section for Reviews & Ratings */}
                                {categoryName === 'Reviews & Ratings' && (() => {
                                  const storedRating = getStoredRatingSummary();

                                  return (
                                  <div className="mb-4 sm:mb-6">
                                    <div className="relative overflow-hidden rounded-2xl border border-purple-100 bg-white shadow-[0_20px_55px_-38px_rgba(15,23,42,0.32)]">
                                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.14),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(243,111,33,0.12),_transparent_30%)]" />
                                      <div className="relative p-5 sm:p-6">
                                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                          <div className="max-w-xl">
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.26em] text-[#9a51a0]">
                                              Community Rating
                                            </p>
                                            <h4 className="text-xl sm:text-2xl font-bold text-slate-900">
                                              Rate This College
                                            </h4>
                                            <p className="mt-2 text-sm sm:text-base leading-7 text-slate-600">
                                              Share your quick impression to help future students compare colleges with more confidence.
                                            </p>
                                          </div>

                                          <div className="inline-flex min-w-[110px] flex-col rounded-2xl border border-orange-100 bg-white/90 px-4 py-3 text-center shadow-sm">
                                            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                              Your Score
                                            </span>
                                            <span className="mt-1 text-2xl font-bold text-slate-900">
                                              {userRating > 0 ? `${userRating}.0` : '0.0'}
                                            </span>
                                            <span className="text-xs text-slate-500">out of 5</span>
                                          </div>
                                        </div>

                                        <div className="mt-6 rounded-2xl border border-white/80 bg-white/85 p-4 sm:p-5 shadow-inner">
                                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div>
                                              <label className="block text-sm font-semibold text-slate-800 mb-1">
                                                Your Rating
                                              </label>
                                              <p className="text-xs sm:text-sm text-slate-500">
                                                Add your quick score to make this profile more useful for other students.
                                              </p>
                                            </div>
                                            <div className="self-start rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-[#9a51a0]">
                                              Quick student feedback
                                            </div>
                                          </div>

                                          <div className="mt-4 flex flex-col items-start gap-3">
                                            <StarRating 
                                              rating={userRating}
                                              size="large"
                                              interactive={true}
                                              onRatingChange={handleRatingChange}
                                              showNumeric={false}
                                              className="mb-1"
                                            />
                                            <p className="text-base sm:text-lg font-semibold text-slate-800">
                                              {userRating > 0 ? `${userRating.toFixed(1)} / 5` : 'Tap a star to rate this college'}
                                            </p>
                                          </div>
                                        </div>

                                        {(storedRating.numericRating !== null || storedRating.reviewText) && (
                                          <div className="mt-4 rounded-2xl border border-slate-200 bg-white/90 p-4 sm:p-5 shadow-sm">
                                            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                              <div className="min-w-0 flex-1">
                                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                                                  Student Review Rating
                                                </p>
                                                <div className="mt-3">
                                                  <StarRating
                                                    rating={storedRating.numericRating || 0}
                                                    size="medium"
                                                    interactive={false}
                                                    showNumeric={true}
                                                  />
                                                </div>
                                                {storedRating.reviewText &&
                                                  storedRating.reviewText !== String(storedRating.numericRating) &&
                                                  storedRating.reviewText !== `${storedRating.numericRating?.toFixed?.(1)}` && (
                                                  <p className="mt-3 text-sm leading-6 text-slate-600 break-words">
                                                    {storedRating.reviewText}
                                                  </p>
                                                )}
                                              </div>
                                              {storedRating.numericRating !== null && (
                                                <div className="inline-flex w-full sm:w-auto sm:min-w-[96px] flex-col rounded-2xl bg-slate-50 px-4 py-3 text-center">
                                                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                                    Average
                                                  </span>
                                                  <span className="mt-1 text-xl font-bold text-slate-900">
                                                    {storedRating.numericRating.toFixed(1)}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {userRating > 0 && (
                                          <div className="mt-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4">
                                            <p className="text-sm text-emerald-800 leading-6">
                                              <strong>Thanks for rating.</strong> Your {userRating}-star input adds a useful signal for students exploring this college.
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  );
                                })()}
                                
                                {/* Placement Statistics with improved responsiveness */}
                                {categoryName === 'Placements & Salary' && (
                                  <div className="mb-4 sm:mb-6">
                                    <div className="w-full overflow-hidden">
                                      <PlacementPieChart 
                                        placementData={profile.placement_percentage || profile.placement_rate || profile.placement_stats || profile.placement}
                                        salaryData={{
                                          average_salary: profile.average_salary || profile.avg_salary || profile.average_package,
                                          highest_salary: profile.highest_salary || profile.max_salary || profile.highest_package || profile.max_package
                                        }}
                                        className="w-full max-w-none"
                                      />
                                    </div>
                                  </div>
                                )}
                                
                                {/* Image Carousel for relevant categories */}
                                {['Location & Campus', 'Infrastructure & Facilities', 'Academics'].includes(categoryName) && (
                                  <div className="mb-4 sm:mb-6">
                                    {(() => {
                                      const images = getCollegeImages(profile, categoryName);
                                      const hasRealImages = images.length > 0 && !images[0]?.isDefault;
                                      
                                      if (hasRealImages) {
                                        // Show real images with carousel
                                        return (
                                          <ImageCarousel 
                                            images={images} 
                                            title={categoryName === 'Academics' ? 'Faculty Section' : `${categoryName} Gallery`}
                                            className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
                                            autoSlide={false}
                                            slideInterval={5000}
                                            category={categoryName}
                                          />
                                        );
                                      } else {
                                        // Show default images with carousel functionality
                                        return (
                                          <ImageCarousel 
                                            images={images.map(img => ({...img, isDefault: true}))} 
                                            title={categoryName === 'Academics' ? 'Faculty Section' : `${categoryName} Gallery`}
                                            className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
                                            autoSlide={false}
                                            slideInterval={5000}
                                            category={categoryName}
                                            isDefaultImages={true}
                                          />
                                        );
                                      }
                                    })()}
                                  </div>
                                )}
                                
                                {showDetailsGrid && (
                                <div className="grid gap-4 sm:gap-6">
                                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 lg:p-6 overflow-hidden">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                                      {visibleCategoryEntries.map(([key, value]) => {
                                        // Rename field display names
                                        let displayName = key.replace(/_/g, ' ');
                                        if (key === 'programs' || key === 'courses') {
                                          displayName = 'Programs/Courses';
                                        }
                                        
                                        return (
                                          <div key={key} className="space-y-2 sm:space-y-3 min-w-0 break-words">
                                            <h4 className="text-xs sm:text-sm font-semibold text-gray-900 capitalize border-b border-gray-100 pb-1 sm:pb-2 mb-2 sm:mb-3 truncate">
                                              {displayName}
                                            </h4>
                                            <div className="text-gray-700 text-xs sm:text-sm break-words overflow-hidden min-h-[1.25rem] sm:min-h-[1.5rem] flex items-start">
                                              {formatValue(value, key)}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 sm:p-8 lg:p-12 text-center border-b border-gray-100">
                  <div className="p-6 sm:p-8 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-6 sm:mb-8 flex items-center justify-center">
                    <MdSchool className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 text-purple-500" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">Discover Colleges</h3>
                  <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8 max-w-lg mx-auto leading-relaxed px-4">
                    Search for colleges by name or use filters to explore institutions by location and ranking.
                  </p>
                </div>
                <div className="p-6 sm:p-8 bg-gray-50">
                  <div className="text-center text-gray-500">
                    <p className="text-xs sm:text-sm">Start by typing a college name or selecting filters above</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </div>
  );
}

export default CollegeProfile;



